'use client';

import { useState, useEffect } from 'react';
import { ChevronUp, Compass, X } from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Accueil', href: '#accueil', icon: 'M' },
  { label: 'Maison', href: '#maison', icon: 'M' },
  { label: 'Héritage', href: '#heritage', icon: 'H' },
  { label: 'Collections', href: '#collections', icon: 'C' },
  { label: 'Atelier', href: '#atelier', icon: 'A' },
  { label: 'Savoir-Faire', href: '#savoir-faire', icon: 'S' },
  { label: 'Lexique', href: '#lexique', icon: 'L' },
  { label: 'Salon Privé', href: '#salon-prive', icon: 'P' },
  { label: 'Distinctions', href: '#distinctions', icon: 'D' },
  { label: 'Galerie', href: '#galerie', icon: 'G' },
  { label: 'Témoignages', href: '#temoignages', icon: 'T' },
  { label: 'Contact', href: '#contact', icon: 'C' },
];

export default function NavButtons() {
  const [showTop, setShowTop] = useState(false);
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('accueil');

  // Show scroll-to-top button after scrolling
  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 500);
      // Detect active section
      const sections = NAV_ITEMS.map((i) => i.href.substring(1));
      let current = 'accueil';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom > 120) {
            current = id;
            break;
          }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href: string) => {
    setShowQuickNav(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Floating nav cluster (above chatbot) */}
      <div className="fixed bottom-6 left-6 z-[55] flex flex-col gap-3 items-start">
        {/* Scroll to top */}
        <button
          onClick={scrollTop}
          className={`w-11 h-11 rounded-full bg-[#2B1810]/95 backdrop-blur-md border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-500 flex items-center justify-center shadow-lg ${
            showTop
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          aria-label="Remonter en haut"
          title="Remonter en haut"
        >
          <ChevronUp size={20} />
        </button>

        {/* Quick nav toggle */}
        <button
          onClick={() => setShowQuickNav(!showQuickNav)}
          className={`w-11 h-11 rounded-full border flex items-center justify-center shadow-lg transition-all duration-500 ${
            showQuickNav
              ? 'bg-[#C9A961] border-[#C9A961] text-[#2B1810] rotate-0'
              : 'bg-[#2B1810]/95 backdrop-blur-md border-[#C9A961]/40 text-[#C9A961] hover:border-[#C9A961]'
          }`}
          aria-label="Navigation rapide"
          title="Navigation rapide"
        >
          {showQuickNav ? <X size={18} /> : <Compass size={18} />}
        </button>
      </div>

      {/* Quick nav panel */}
      <div
        className={`fixed bottom-20 left-6 z-[55] transition-all duration-300 ${
          showQuickNav
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="bg-[#2B1810]/95 backdrop-blur-md border border-[#C9A961]/40 rounded-2xl shadow-2xl p-3 w-[200px]">
          <p className="text-[#C9A961] text-[9px] uppercase tracking-[0.3em] font-serif-display text-center mb-3 pb-2 border-b border-[#C9A961]/20">
            Navigation
          </p>
          <div className="flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto">
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.href.substring(1);
              return (
                <button
                  key={item.href}
                  onClick={() => scrollTo(item.href)}
                  className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-300 ${
                    isActive
                      ? 'bg-[#C9A961]/15 text-[#C9A961]'
                      : 'text-[#F8F1E4]/80 hover:bg-[#C9A961]/10 hover:text-[#C9A961]'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-display font-semibold transition-colors ${
                      isActive
                        ? 'border-[#C9A961] bg-[#C9A961] text-[#2B1810]'
                        : 'border-[#C9A961]/40 group-hover:border-[#C9A961]'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span className="text-xs tracking-[0.12em] uppercase font-medium flex-1">
                    {item.label}
                  </span>
                  {isActive && (
                    <span className="w-1 h-1 rounded-full bg-[#C9A961] animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
